import { useCallback, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageShell } from '~/components/layout';
import {
  CardTransactionsPanel,
  CtaBanner,
  FaqAccordion,
  FeatureBlock,
  Hero,
  OfferBanner,
  TechCashback,
  TechOfferModal,
  TextSection,
} from '~/components/sections';
import { TransactionToast } from '~/components/ui';
import { payrollFaqFootnote } from '~/content/payroll';
import {
  techCardTransactions,
  techDigitalSpending,
  techFaq,
  techHero,
  techHeroTransaction,
  techIdeasSection,
  techOfferBanner,
  techPromptsToProjects,
} from '~/content/tech';
import { faqPageJsonLd, pageHeadMeta } from '~/lib/seo';
import heroCss from '~/styles/hero.css?inline';

export const Route = createFileRoute('/cards_/tech')({
  head: () => {
    const head = pageHeadMeta({
      title: 'The nsave Card for Your Tech Stack',
      description:
        'Pay for ChatGPT, Claude, Figma, GitHub and the rest of your tech stack in USD with the nsave card. Get 50% cashback, up to $5, on your first ChatGPT or Claude payment.',
      path: '/cards/tech',
      inlineStyles: [heroCss],
    });
    return { ...head, scripts: [...head.scripts, faqPageJsonLd(techFaq)] };
  },
  component: TechPage,
});

function TechPage() {
  const [offerOpen, setOfferOpen] = useState(false);
  const closeOffer = useCallback(() => setOfferOpen(false), []);

  return (
    <PageShell>
      <Hero
        content={techHero}
        // Figma: 36px (mobile) / 56px headline, CTA 40px / 48px below it.
        titleClassName="!text-[36px] !leading-[1.16] !tracking-[-0.022em] md:!text-[56px] md:max-w-[561px]"
        ctaClassName="max-md:!mt-10 md:!mt-12"
        // Mobile Figma: a 560px image. The stage frame matches it, since the
        // portrait crop has no spare height to fill the default 800px frame.
        mediaClassName="max-md:!h-[560px] max-md:[--hero-h-start:560px]"
        statsSlot={
          <OfferBanner
            {...techOfferBanner}
            onLearnMore={() => setOfferOpen(true)}
            className="mt-[88px] md:mt-[138px]"
          />
        }
        imageOverlay={
          // Toast drawn at 0.889 (mobile) / 1.418 (desktop) of the 79px design toast.
          <div className="absolute bottom-[91px] left-6 text-[length:0.889px] md:top-[230px] md:right-[8.2%] md:bottom-auto md:left-auto md:text-[length:1.418px]">
            <TransactionToast {...techHeroTransaction} width={299} />
          </div>
        }
      />
      <TextSection content={techIdeasSection} variant="landing" sectionClassName="max-md:!my-10" />
      <FeatureBlock
        content={techPromptsToProjects}
        variant="landing"
        imagePosition="left"
        media={
          <CardTransactionsPanel
            transactions={techCardTransactions}
            tileClassName="rounded-[12em] bg-[#f0f0f0]"
          />
        }
        sectionClassName="!pt-0 !pb-[75px] lg:!pb-5"
      />
      <FeatureBlock
        content={techDigitalSpending}
        variant="landing"
        imagePosition="right"
        sectionClassName="!pt-0 !pb-[75px] lg:!pb-[200px]"
      />
      <TechCashback className="!py-0" />
      <FaqAccordion
        content={techFaq}
        variant="landing"
        gridAligned
        toggleIcon="chevron"
        footnote={payrollFaqFootnote}
        sectionClassName="!pt-[115px] !pb-[60px] lg:!pt-[180px] lg:!pb-[144px]"
      />
      <CtaBanner />
      {/* Page level rather than inside the banner: the hero is its own stacking context. */}
      <TechOfferModal open={offerOpen} onClose={closeOffer} />
    </PageShell>
  );
}
